import {
  MattersStyleTableShell,
  useMattersStyleTableColumnDragSensors,
} from "@/components/features/matters/matters-style-table/MattersStyleTableShell";
import { AddOrEditFirmPhoneModal } from "@/components/features/settings/firm/AddOrEditFirmPhoneModal";
import { Text } from "@/components/ui";
import { useFirmPhones } from "@/lib/data/react-query/firm/firm-phones";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { type FirmPhone } from "@/lib/types/Firm";
import { customDateTimeSortingFns } from "@/lib/utils/tableUtils";
import {
  persistTanstackTablePrefs,
  readTanstackTablePrefs,
} from "@/lib/utils/tanstack-table-local-storage";
import type { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { ErrorBoundary } from "@sentry/react";
import {
  type ColumnDef,
  type SortingState,
  type VisibilityState,
  functionalUpdate,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo, useState } from "react";

const FIRM_PHONE_TABLE_COLUMN_IDS = ["Label", "Phone Number"] as const;

const FIRM_PHONES_TABLE_LS_KEY: string = tanstackTableNames.firmPhones;

function FirmPhonesTableDangerous({
  isAdding,
  setIsAdding,
}: {
  isAdding: boolean;
  setIsAdding: (open: boolean) => void;
}) {
  const {
    data: firmPhones,
    isLoading: isFirmPhonesLoading,
    isError: isFirmPhonesError,
  } = useFirmPhones();

  const [editingPhone, setEditingPhone] = useState<FirmPhone | undefined>();
  const [searchQuery, setSearchQuery] = useState("");

  const [sorting, setSorting] = useState<SortingState>(() => {
    const stored = readTanstackTablePrefs(FIRM_PHONES_TABLE_LS_KEY);
    return stored.sorting ?? [];
  });
  const [pagination, setPagination] = useState(() => {
    const stored = readTanstackTablePrefs(FIRM_PHONES_TABLE_LS_KEY);
    return {
      pageIndex: 0,
      pageSize: stored.pagination?.pageSize ?? 10,
    };
  });
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const stored = readTanstackTablePrefs(FIRM_PHONES_TABLE_LS_KEY);
    const ids = [...FIRM_PHONE_TABLE_COLUMN_IDS];
    const order = stored.columnOrder;
    if (order?.length) {
      return [...order, ...ids.filter((id) => !order.includes(id))];
    }
    return ids;
  });
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const stored = readTanstackTablePrefs(FIRM_PHONES_TABLE_LS_KEY);
      return {
        ...stored.columnVisibility,
        Label: true,
      };
    },
  );

  const firmPhoneRows = useMemo(() => firmPhones?.data ?? [], [firmPhones]);

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return firmPhoneRows;
    return firmPhoneRows.filter((row) => {
      const label = row.label.toLowerCase();
      const phone = (row.friendlyName ?? "").toLowerCase();
      const number = row.number;
      return label.includes(q) || phone.includes(q) || number.includes(q);
    });
  }, [firmPhoneRows, searchQuery]);

  const columns = useMemo<ColumnDef<FirmPhone>[]>(
    () => [
      {
        id: "Label",
        accessorFn: (row) => row.label,
        header: "Label",
        enableHiding: false,
        cell: ({ row }) => (
          <Text variant="body" className="font-semibold">
            {row.original.label}
          </Text>
        ),
        sortingFn: "alphanumeric",
      },
      {
        id: "Phone Number",
        accessorFn: (row) => row.number,
        header: "Phone number",
        cell: ({ row }) => (
          <div className="space-y-0.5">
            <Text variant="subHeading">{row.original.number}</Text>
            {row.original.friendlyName ? (
              <Text variant="caption">{row.original.friendlyName}</Text>
            ) : null}
          </div>
        ),
        sortingFn: "alphanumeric",
      },
    ],
    [],
  );

  const table = useReactTable({
    data: filteredRows,
    columns,
    autoResetPageIndex: true,
    state: {
      sorting,
      columnOrder,
      columnVisibility,
      pagination,
    },
    onSortingChange: (updater) => {
      setSorting((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(FIRM_PHONES_TABLE_LS_KEY, {
          sorting: next,
        });
        return next;
      });
    },
    onPaginationChange: (updater) => {
      setPagination((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(FIRM_PHONES_TABLE_LS_KEY, {
          pagination: { pageSize: next.pageSize },
        });
        return next;
      });
    },
    onColumnOrderChange: (updater) => {
      setColumnOrder((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(FIRM_PHONES_TABLE_LS_KEY, {
          columnOrder: next,
        });
        return next;
      });
    },
    onColumnVisibilityChange: (updater) => {
      setColumnVisibility((prev) => {
        const next = functionalUpdate(updater, prev);
        const merged = { ...next, Label: true };
        persistTanstackTablePrefs(FIRM_PHONES_TABLE_LS_KEY, {
          columnVisibility: merged,
        });
        return merged;
      });
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    sortingFns: customDateTimeSortingFns,
  });

  const sensors = useMattersStyleTableColumnDragSensors();

  const handleColumnDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setColumnOrder((prev) => {
      const oldIndex = prev.indexOf(active.id as string);
      const newIndex = prev.indexOf(over.id as string);
      if (oldIndex < 0 || newIndex < 0) return prev;
      const next = arrayMove(prev, oldIndex, newIndex);
      persistTanstackTablePrefs(FIRM_PHONES_TABLE_LS_KEY, {
        columnOrder: next,
      });
      return next;
    });
  };

  return (
    <div>
      <MattersStyleTableShell
        table={table}
        columnOrder={columnOrder}
        columnIdCountBaseline={FIRM_PHONE_TABLE_COLUMN_IDS.length}
        onColumnDragEnd={handleColumnDragEnd}
        sensors={sensors}
        search={{
          value: searchQuery,
          onChange: (value) => {
            setSearchQuery(value);
            setPagination((p) => ({ ...p, pageIndex: 0 }));
          },
          placeholder: "Search phone numbers...",
        }}
        isLoading={isFirmPhonesLoading}
        isError={isFirmPhonesError}
        errorMessage={serverErrorFallbackText}
        emptyMessage="No phone numbers found."
        totalFilteredRowCount={filteredRows.length}
        entityNamePlural="phone numbers"
        sorting={sorting}
        tableProps={{
          "data-testid": tanstackTableNames.firmPhones,
          "data-table-name": tanstackTableNames.firmPhones,
        }}
        getTableRowProps={(row) => ({
          "data-testid": "table-row",
          className: "cursor-pointer",
          onClick: () => {
            setEditingPhone(row.original);
          },
        })}
      />

      <AddOrEditFirmPhoneModal
        open={isAdding}
        setOpen={(open) => {
          if (!open) {
            setIsAdding(false);
            setEditingPhone(undefined);
          }
        }}
        phone={editingPhone}
      />
      {editingPhone ? (
        <AddOrEditFirmPhoneModal
          open={!!editingPhone}
          setOpen={(open) => {
            if (!open) {
              setEditingPhone(undefined);
            }
          }}
          phone={editingPhone}
        />
      ) : null}
    </div>
  );
}

export function FirmPhonesTable({
  isAdding,
  setIsAdding,
}: {
  isAdding: boolean;
  setIsAdding: (open: boolean) => void;
}) {
  return (
    <ErrorBoundary
      fallback={<Text variant="subHeading">{serverErrorFallbackText}</Text>}
    >
      <FirmPhonesTableDangerous isAdding={isAdding} setIsAdding={setIsAdding} />
    </ErrorBoundary>
  );
}
